import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { buyerUserContext } from 'application/contexts/useBuyerUser';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { BuyerUserRepository } from 'interfaceAdapter/repositories/BuyerUser';
import { BuyerUserQuery } from 'interfaceAdapter/queries/BuyerUser';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        email: yup.string().required().email(),
        password: yup.string().required(),
    })
    .required();

type LoginForm = yup.InferType<typeof schema>;

export const useLoginForm = () => {
    const navigate = useNavigate();
    const diContainer = useContext(diContainerContext);
    const buyerUserRepository = diContainer.resolve(BuyerUserRepository);
    const buyerUserQuery = diContainer.resolve(BuyerUserQuery);
    const { setUser, urlAfterLogin } = useContext(buyerUserContext);
    const { loading, setLoading } = useLoading();
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const redirectIfLoggedIn = useCallback(async () => {
        const { user, status } = await buyerUserQuery.checkToken(true);
        if (status !== false) {
            setUser(user);
            navigate(urlAfterLogin);
        }
    }, [urlAfterLogin]);

    const login = handleSubmit(async ({ email, password }) => {
        setLoading(true);
        try {
            const user = await buyerUserRepository.loginByEmail(email, password);
            // @ts-ignore
            setUser(user);
            navigate(urlAfterLogin);
        } finally {
            setLoading(false);
        }
    });

    const register = () => {
        navigate('/buyer/send-registration-mail/');
    };

    return {
        control,
        errors,
        loading,
        redirectIfLoggedIn,
        login,
        register,
    };
};
